import React, { useEffect } from 'react';
import { AlertTriangle, Archive, Banknote, ClipboardList, Trash2 } from 'lucide-react';

import DefaultLayout from '../../layout/DefaultLayout';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import { useCashRegistryReset } from '../../hooks/cash/useCashRegistryReset';

const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
}> = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl border border-stroke bg-whiten p-4 dark:border-strokedark dark:bg-meta-4">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      {icon}
    </div>
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-body">
        {label}
      </div>
      <div className="text-xl font-bold text-black dark:text-white">
        {value}
      </div>
    </div>
  </div>
);

const CashResetPage: React.FC = () => {
  const { summary, lastResult, isLoading, handlerLoadSummary, handlerResetAll } =
    useCashRegistryReset();

  useEffect(() => {
    handlerLoadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <div className="space-y-6 p-6">
          {/* 🧭 Título */}
          <div>
            <h2 className="text-xl font-semibold text-black dark:text-white">
              Limpieza de Caja
            </h2>
            <p className="mt-1 text-gray-600 dark:text-gray-300">
              Elimina en bloque todas las aperturas/sesiones, cortes X/Z y
              movimientos de caja registrados en el sistema. Pensado como
              herramienta temporal para que la empresa pueda reiniciar sus
              pruebas de caja las veces que necesite, hasta terminar de
              configurar el módulo.
            </p>
          </div>

          {/* ⚠️ Aviso */}
          <div className="flex gap-3 rounded-xl border border-warning/30 bg-warning/10 p-4 text-sm text-black dark:text-white">
            <AlertTriangle className="h-5 w-5 shrink-0 text-warning" />
            <div>
              <p className="font-semibold">Las ventas nunca se eliminan.</p>
              <p className="mt-1 text-body">
                Esta limpieza solo borra los registros de caja (aperturas,
                cierres y movimientos). Si alguna venta quedó vinculada a una
                sesión eliminada, únicamente se suelta esa referencia — la
                venta, su DTE y su historial permanecen intactos.
              </p>
            </div>
          </div>

          {/* 📊 Resumen actual */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              icon={<Archive size={20} />}
              label="Sesiones de caja"
              value={summary?.sessionsCount ?? 0}
            />
            <StatTile
              icon={<ClipboardList size={20} />}
              label="Cortes X/Z"
              value={summary?.closuresCount ?? 0}
            />
            <StatTile
              icon={<Banknote size={20} />}
              label="Movimientos"
              value={summary?.movementsCount ?? 0}
            />
            <StatTile
              icon={<AlertTriangle size={20} />}
              label="Ventas vinculadas"
              value={summary?.salesLinkedCount ?? 0}
            />
          </div>

          {/* 🗑️ Acción */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handlerResetAll}
              disabled={isLoading}
              className="flex items-center gap-2 rounded-md bg-danger px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 size={16} />
              Eliminar todos los registros de caja
            </button>
          </div>

          {/* ✅ Último resultado */}
          {lastResult && (
            <div className="rounded-xl border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
              <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                Última limpieza realizada
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div>
                  <div className="text-body">Sesiones eliminadas</div>
                  <div className="font-semibold text-black dark:text-white">
                    {lastResult.sessionsDeleted}
                  </div>
                </div>
                <div>
                  <div className="text-body">Cortes eliminados</div>
                  <div className="font-semibold text-black dark:text-white">
                    {lastResult.closuresDeleted}
                  </div>
                </div>
                <div>
                  <div className="text-body">Movimientos eliminados</div>
                  <div className="font-semibold text-black dark:text-white">
                    {lastResult.movementsDeleted}
                  </div>
                </div>
                <div>
                  <div className="text-body">Ventas desvinculadas</div>
                  <div className="font-semibold text-black dark:text-white">
                    {lastResult.salesUnlinked}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </SectionWrapper>
    </DefaultLayout>
  );
};

export default CashResetPage;
